<template>
  <div
    class="pdf-preview"
    :class="{ 'pdf-preview--fullscreen': fullscreen, 'pdf-preview--fill': fill }"
  >
    <!-- Fullscreen puts Close at the top: the bottom-right corner is where the
         Intercom launcher floats, and it sits right on top of a footer button. -->
    <div
      v-if="fullscreen"
      class="pdf-preview__header"
    >
      <span class="text-caption">
        <template v-if="isTruncated">
          Showing the first {{ maxPages }} of {{ pageCount }} pages
        </template>
        <template v-else>
          {{ pageCount > 1 ? `${pageCount} pages` : 'Document' }}
        </template>
      </span>

      <div class="row items-center no-wrap">
        <q-btn
          v-if="isTruncated && src"
          flat
          dense
          no-caps
          size="sm"
          icon="mdi-open-in-new"
          label="Open full document"
          @click="openDocument"
        />

        <q-btn
          flat
          dense
          no-caps
          size="sm"
          icon="mdi-fullscreen-exit"
          label="Close"
          @click="toggleFullscreen"
        />
      </div>
    </div>

    <!-- Loading -->
    <div
      v-if="loading"
      class="pdf-preview__state"
    >
      <q-spinner size="2em" />
      <div class="text-caption q-mt-sm">
        {{ loadingLabel }}
      </div>
    </div>

    <!-- On a phone nothing is drawn: an A4 page is unreadable at that width,
         and the phone renders the PDF itself, with text selection, search and
         proper zoom. Drawing even a first-page preview meant fetching the
         whole file, which the phone then fetched again to show it; and the
         canvas viewer cannot match the phone's for sharpness or panning once
         zoomed. A card names the document instead; it and the Review button
         under it open the file. -->
    <div
      v-if="thumbnailMode"
      class="pdf-preview__card"
      @click="openDocument"
    >
      <q-icon
        name="mdi-file-pdf-box"
        size="2.5em"
        color="red-7"
      />
      <div class="col">
        <div class="text-subtitle2">
          {{ title }}
        </div>
        <div class="text-caption">
          PDF document
        </div>
      </div>
    </div>

    <!-- Rendered pages. The inner layer is what a pinch scales; the outer one
         scrolls, so the zoomed pages can be panned with one finger. -->
    <div
      v-show="!thumbnailMode && !loading && !failed"
      ref="pages"
      class="pdf-preview__pages"
      @touchstart.passive="onTouchStart"
      @touchmove="onTouchMove"
      @touchend.passive="onTouchEnd"
      @touchcancel.passive="onTouchEnd"
    >
      <div
        ref="zoomLayer"
        class="pdf-preview__zoom"
      />
    </div>

    <q-btn
      v-if="thumbnailMode"
      class="full-width q-mt-sm"
      unelevated
      no-caps
      color="primary"
      size="16px"
      padding="sm md"
      icon="mdi-file-document-outline"
      label="Review document"
      :loading="opening"
      @click="openDocument"
    />

    <!-- Could not render inline: offer the file itself rather than a dead end -->
    <div
      v-if="failed"
      class="pdf-preview__state"
    >
      <q-icon
        name="mdi-file-document-alert-outline"
        size="2.5em"
        color="grey-7"
      />
      <div class="text-subtitle2 q-mt-sm">
        Preview unavailable
      </div>
      <div class="text-caption q-mt-xs">
        This document could not be shown here.
      </div>
      <q-btn
        v-if="src"
        class="q-mt-md"
        outline
        no-caps
        icon="mdi-open-in-new"
        label="Open document"
        @click="openDocument"
      />
    </div>

    <!-- A 500px-wide guest card is far too narrow to read an A4 report in, so
         offer the whole viewport rather than only a thumbnail-sized preview. -->
    <div
      v-if="!thumbnailMode && !loading && !failed && !fullscreen && (canFullscreen || isTruncated || pageCount > 1)"
      class="pdf-preview__footer"
    >
      <span
        v-if="isTruncated"
        class="text-caption"
      >
        Showing the first {{ maxPages }} of {{ pageCount }} pages
      </span>
      <span
        v-else-if="pageCount > 1"
        class="text-caption"
      >
        {{ pageCount }} pages
      </span>
      <span v-else />

      <div class="row items-center no-wrap">
        <q-btn
          v-if="isTruncated && src"
          flat
          dense
          no-caps
          size="sm"
          icon="mdi-open-in-new"
          label="Open full document"
          @click="openDocument"
        />

        <!-- A phone has no bigger view to offer: the viewer already spans the
             screen, and pinching zooms in. -->
        <q-btn
          v-if="canFullscreen"
          flat
          dense
          no-caps
          size="sm"
          icon="mdi-fullscreen"
          label="View full screen"
          @click="toggleFullscreen"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { openPdfNatively } from 'src/helpers/nativeFile'

/**
 * Inline PDF preview.
 *
 * Renders every page to a canvas via pdf.js rather than handing the file to the
 * browser. An <iframe> is simpler, and files/view/filetypes/TypePdf.vue still
 * uses one on desktop where it is better -- native toolbar, text selection,
 * search. But mobile Safari and the Android WebView will not display a PDF
 * inline at all, so anything small needs this. Canvas rendering behaves the same
 * everywhere, which matters where a document has to be read in the window it is
 * signed in.
 *
 * pdf.js is imported dynamically so its ~1MB stays out of the main bundle and is
 * only fetched by the routes that actually show a document. The legacy build is
 * deliberate: it pulls the core-js polyfill for Promise.withResolvers, which the
 * modern build assumes (Safari 17.4+) and older client devices do not have.
 */

// Cap the device pixel ratio. Rendering a long report at DPR 3 on a phone will
// exhaust canvas memory; 2 is already sharper than the screen can show.
const MAX_SCALE = 2

// Widest a page is laid out, whatever the container. A4 is legible well before
// this, and without the cap a fullscreen render on a wide display rasterises
// every page to several thousand pixels -- which is how a phone runs out of
// canvas memory on a long report.
const MAX_PAGE_WIDTH = 1100

// Pinch zoom ceiling. Pages are rasterised at up to DPR 2, so 3x is where
// they start to soften; past that the memory cost outweighs the legibility.
const MAX_ZOOM = 3

function touchDistance (touches) {
  return Math.hypot(touches[0].clientX - touches[1].clientX, touches[0].clientY - touches[1].clientY)
}

function touchMidpoint (touches) {
  return {
    x: (touches[0].clientX + touches[1].clientX) / 2,
    y: (touches[0].clientY + touches[1].clientY) / 2
  }
}

export default {
  name: 'PdfPreview',

  props: {
    src: {
      type: String,
      default: null
    },
    // What the document is, for the phone card that stands in for a preview.
    title: {
      type: String,
      default: 'Document'
    },
    // Hard ceiling on pages rendered, so a 200-page document cannot hang a phone.
    maxPages: {
      type: Number,
      default: 30
    },
    // Fill the height available instead of capping at 60vh. For a dedicated
    // viewer, where the preview is the whole screen rather than one item on a
    // page of them.
    fill: {
      type: Boolean,
      default: false
    }
  },

  emits: ['openExternally', 'rendered', 'failed'],

  data () {
    return {
      loading: false,
      failed: false,
      pageCount: 0,
      loadingLabel: 'Loading document…',
      fullscreen: false,
      renderedWidth: 0,
      renderedPages: 0,
      zoom: 1,
      opening: false
    }
  },

  computed: {
    // The page count is the document's, not what was drawn. Saying "50 pages"
    // while showing 30 reads as though the rest simply were not there.
    isTruncated () {
      return this.renderedPages > 0 && this.pageCount > this.renderedPages
    },

    // Fullscreen only escapes a cramped card on a desktop. On a phone the
    // viewer already has the width of the screen, and pinching does the rest.
    canFullscreen () {
      return this.$q.platform.is.desktop
    },

    // A mobile browser, including the in-app browser a client's link opens
    // in. Not the app's own webview: navigating that away from the SPA would
    // be a dead end, so the app hands the file to the OS instead.
    canOpenInBrowser () {
      return this.$q.platform.is.mobile && !this.$q.platform.is.nativeMobile
    },

    // Phones get a card over a Review button, and never fetch the file. A
    // dedicated viewer (fill) already has the whole screen, so it draws every
    // page.
    thumbnailMode () {
      return this.$q.platform.is.mobile && !this.fill
    }
  },

  watch: {
    src: {
      immediate: true,
      handler (val) {
        if (val) {
          this.$nextTick(this.render)
        }
      }
    }
  },

  methods: {
    // Hand the file to the phone. Also the fallback when the document could
    // not be drawn, and the way to the pages past maxPages.
    //
    // In the app, Quick Look (or Android's PDF handler), whose Done returns
    // here. On a phone browser, navigate in place: it keeps a Custom Tab from
    // spilling into the full browser, and Back returns to this page.
    async openDocument () {
      if (this.$q.platform.is.nativeMobile) {
        this.opening = true
        await openPdfNatively(this.src, `documents/${this.title.replace(/[/\\]/g, '_')}.pdf`)
        this.opening = false
        return
      }
      if (this.canOpenInBrowser) {
        window.location.assign(this.src)
        return
      }
      this.$emit('openExternally', this.src)
    },

    // Cancel in-flight page paints. Deliberately leaves the document loaded: a
    // width change only needs the pages drawn again, not the file fetched again.
    cancelPaints () {
      if (this.renderTasks) {
        this.renderTasks.forEach(task => {
          try {
            task.cancel()
          }
          catch (err) {
            // Already settled — nothing to cancel.
          }
        })
      }
      this.renderTasks = []
    },

    // Only when the source changes or the component goes away. pdf.js tears down
    // the worker wrapper bound to our workerPort here, so anything loaded after
    // this needs a fresh worker.
    destroyDocument () {
      this.cancelPaints()

      if (this.pdf) {
        try {
          this.pdf.destroy()
        }
        catch (err) {
          console.error('failed to destroy pdf document', err)
        }
        this.pdf = null
      }

      this.terminateWorker()
    },

    // A repaint reuses the worker; only a source change or unmount ends it.
    terminateWorker () {
      if (this.worker) {
        this.worker.terminate()
        this.worker = null
      }
    },

    // Re-render after the container resizes: the canvas is rasterised to a fixed
    // pixel width, so scaling it up with CSS alone would just blur it.
    async toggleFullscreen () {
      this.fullscreen = !this.fullscreen

      if (this.fullscreen) {
        document.addEventListener('keydown', this.onKeydown)
      }
      else {
        document.removeEventListener('keydown', this.onKeydown)
      }

      await this.$nextTick()
      // Repaint at the new width from the document already in memory.
      this.paint()
    },

    // A canvas is rasterised at a fixed pixel width, so a container that changes
    // width (window resize, phone rotation, a layout change over HMR) leaves the
    // page either blurry or too small until it is drawn again.
    onResize () {
      clearTimeout(this.resizeTimer)
      this.resizeTimer = setTimeout(() => {
        const container = this.$refs.pages
        if (!container || !this.src || this.loading) {
          return
        }

        const style = window.getComputedStyle(container)
        const padding = parseFloat(style.paddingLeft || 0) + parseFloat(style.paddingRight || 0)
        const available = Math.max(container.clientWidth - padding, 240)
        const width = Math.min(available, MAX_PAGE_WIDTH)

        // Ignore the small jitter that scrollbars and rounding produce.
        if (Math.abs(width - this.renderedWidth) > 24) {
          this.paint()
        }
      }, 250)
    },

    onKeydown (e) {
      if (e.key === 'Escape' && this.fullscreen) {
        this.toggleFullscreen()
      }
    },

    // Pinch to zoom. The app's viewport is user-scalable=no, so a two-finger
    // pinch does nothing on a phone unless the viewer handles it itself. The
    // pages are scaled with a transform, which also grows the scroll area, and
    // the scroll position is moved so the point between the fingers stays put.
    onTouchStart (e) {
      if (e.touches.length !== 2) {
        return
      }

      const container = this.$refs.pages
      const rect = container.getBoundingClientRect()
      const style = window.getComputedStyle(container)
      const mid = touchMidpoint(e.touches)
      const padding = {
        left: parseFloat(style.paddingLeft || 0),
        top: parseFloat(style.paddingTop || 0)
      }

      this.pinch = {
        distance: touchDistance(e.touches),
        zoom: this.zoom,
        padding,
        // The unscaled page point under the fingers, which zooming keeps there.
        x: (mid.x - rect.left + container.scrollLeft - padding.left) / this.zoom,
        y: (mid.y - rect.top + container.scrollTop - padding.top) / this.zoom
      }
    },

    onTouchMove (e) {
      if (!this.pinch || e.touches.length !== 2) {
        return
      }
      // Otherwise the scroller pans with the pinch and the browser tries its
      // own (disabled) zoom.
      e.preventDefault()

      const zoom = Math.min(Math.max(this.pinch.zoom * touchDistance(e.touches) / this.pinch.distance, 1), MAX_ZOOM)
      this.applyZoom(zoom)

      const container = this.$refs.pages
      const rect = container.getBoundingClientRect()
      const mid = touchMidpoint(e.touches)
      container.scrollLeft = this.pinch.x * zoom + this.pinch.padding.left - (mid.x - rect.left)
      container.scrollTop = this.pinch.y * zoom + this.pinch.padding.top - (mid.y - rect.top)
    },

    onTouchEnd (e) {
      if (e.touches.length >= 2) {
        return
      }
      this.pinch = null
      // Snap back rather than leaving a page a hair wider than the screen.
      if (this.zoom < 1.05) {
        this.applyZoom(1)
      }
    },

    // Set directly on the element rather than through a binding: a pinch fires
    // many moves a second, and the scroll correction needs the transform to be
    // in place in the same frame.
    applyZoom (zoom) {
      this.zoom = zoom
      const layer = this.$refs.zoomLayer
      if (layer) {
        layer.style.transform = zoom === 1 ? '' : `scale(${zoom})`
      }
    },

    /**
     * Fetch the document, then draw it. Only called when the source changes --
     * a width change goes straight to paint().
     */
    async render () {
      if (!this.src || this.thumbnailMode) {
        return
      }

      this.destroyDocument()
      this.loading = true
      this.failed = false
      this.loadingLabel = 'Loading document…'

      const generation = (this.generation || 0) + 1
      this.generation = generation
      const isStale = () => this.generation !== generation

      try {
        const pdfjs = await import('pdfjs-dist/legacy/build/pdf.mjs')

        // Hand pdf.js a Worker we construct ourselves rather than a workerSrc URL.
        //
        // The documented `workerSrc = new URL('...pdf.worker.min.mjs', import.meta.url)`
        // does not survive this bundler: webpack resolves that specifier as a
        // JavaScript module and inlines its exports, so the URL stringifies to
        // "[object Object]", the worker never spawns, and pdf.js quietly falls back
        // to parsing on the main thread. `new Worker(new URL(...))` is the pattern
        // webpack 5 actually recognises -- it emits the worker as its own chunk.
        if (!this.worker) {
          this.worker = new Worker(
            new URL('pdfjs-dist/legacy/build/pdf.worker.min.mjs', import.meta.url),
            { type: 'module' }
          )
        }
        pdfjs.GlobalWorkerOptions.workerPort = this.worker

        const pdf = await pdfjs.getDocument({ url: this.src }).promise
        if (isStale()) {
          pdf.destroy()
          return
        }
        this.pdf = pdf
        this.pageCount = pdf.numPages

        await this.paint()
      }
      catch (err) {
        if (this.generation !== generation) {
          return
        }
        console.error('failed to load pdf', err)
        this.loading = false
        this.failed = true
        this.$emit('failed', err)
      }
    },

    /**
     * Draw the already-loaded document at the container's current width.
     *
     * Separate from render() so a width change -- the fullscreen toggle, a
     * rotation -- repaints from memory instead of destroying the document and
     * fetching it again. Re-fetching was also what broke fullscreen: pdf.js
     * tears down the worker bound to our workerPort on destroy(), so the
     * reload failed and surfaced as "Preview unavailable".
     */
    async paint () {
      const pdf = this.pdf
      if (!pdf) {
        return
      }

      const generation = (this.generation || 0) + 1
      this.generation = generation
      const isStale = () => this.generation !== generation

      this.cancelPaints()
      this.loading = true
      this.failed = false

      const container = this.$refs.pages
      const layer = this.$refs.zoomLayer
      if (layer) {
        layer.innerHTML = ''
      }
      // The raster is being redrawn to fit the new width; a stale zoom on top
      // of it would just be blur.
      this.applyZoom(1)

      try {
        const pages = Math.min(pdf.numPages, this.maxPages)
        this.renderedPages = pages
        // The container can still be mid-layout on first paint, which would fit
        // every page to a width of zero. Give it a frame before measuring.
        if (container && container.clientWidth === 0) {
          await new Promise(resolve => requestAnimationFrame(resolve))
          if (isStale()) {
            return
          }
        }

        // clientWidth includes the container's own padding, so take it back off
        // before fitting the page to it.
        const style = container ? window.getComputedStyle(container) : null
        const padding = style
          ? parseFloat(style.paddingLeft || 0) + parseFloat(style.paddingRight || 0)
          : 0
        const available = Math.max((container?.clientWidth || 800) - padding, 240)
        const width = Math.min(available, MAX_PAGE_WIDTH)
        this.renderedWidth = width
        const scale = Math.min(window.devicePixelRatio || 1, MAX_SCALE)

        for (let n = 1; n <= pages; n++) {
          this.loadingLabel = pages > 1 ? `Loading page ${n} of ${pages}…` : 'Loading document…'

          if (isStale()) {
            return
          }

          const page = await pdf.getPage(n)
          const base = page.getViewport({ scale: 1 })
          // Fit the page to the container, then oversample for a crisp raster.
          const viewport = page.getViewport({ scale: (width / base.width) * scale })

          const canvas = document.createElement('canvas')
          canvas.width = viewport.width
          canvas.height = viewport.height
          canvas.className = 'pdf-preview__page'
          // Lay out at container width; the extra pixels are for sharpness only.
          // Fill the container but never exceed the render width: a fixed px
          // width is measured before the vertical scrollbar exists, so it ends up
          // overflowing once enough pages are added to make the list scroll.
          canvas.style.width = '100%'
          canvas.style.maxWidth = width + 'px'

          // The container can disappear if the component unmounts mid-render.
          if (!this.$refs.zoomLayer) {
            return
          }
          this.$refs.zoomLayer.appendChild(canvas)

          const task = page.render({
            canvasContext: canvas.getContext('2d'),
            viewport
          })
          this.renderTasks.push(task)
          await task.promise
        }

        if (isStale()) {
          return
        }

        this.loading = false
        this.$emit('rendered', { pages: this.pageCount })
      }
      catch (err) {
        // A cancelled render is expected when re-rendering or unmounting.
        // A cancelled render, or one superseded by a newer call, is expected.
        if (isStale() || (err && err.name === 'RenderingCancelledException')) {
          return
        }
        console.error('failed to render pdf preview', err)
        this.loading = false
        this.failed = true
        this.$emit('failed', err)
      }
    }
  },

  mounted () {
    window.addEventListener('resize', this.onResize)
  },

  beforeUnmount () {
    clearTimeout(this.resizeTimer)
    window.removeEventListener('resize', this.onResize)
    document.removeEventListener('keydown', this.onKeydown)
    this.destroyDocument()
  }
}
</script>

<style scoped lang="scss">
.pdf-preview {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.pdf-preview__state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 2.5rem 1rem;
  color: $grey-8;
}

.pdf-preview__pages {
  width: 100%;
  max-height: 60vh;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  // One finger pans; two are the viewer's own pinch, not the browser's.
  touch-action: pan-x pan-y;
  background: $grey-3;
  padding: 0.75rem;
  border-radius: 4px;
}

/* The phone stand-in for a preview. */
.pdf-preview__card {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: $grey-3;
  border-radius: 4px;
  cursor: pointer;
}

.pdf-preview__zoom {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  transform-origin: 0 0;
}

.pdf-preview__pages :deep(.pdf-preview__page) {
  display: block;
  height: auto;
  background: white;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);
}

.pdf-preview__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding-bottom: 0.5rem;
  color: $grey-7;
  flex: none;
}

.pdf-preview__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding-top: 0.5rem;
  color: $grey-7;
}

/* Escape the 500px guest card and use the whole viewport. */
.pdf-preview--fullscreen {
  position: fixed;
  inset: 0;
  z-index: 6000;
  background: white;
  padding: 0.5rem 0.75rem 0.75rem;
}

.pdf-preview--fullscreen .pdf-preview__pages,
.pdf-preview--fill .pdf-preview__pages {
  max-height: none;
  flex: 1;
  min-height: 0;
}

/* The viewer is the whole screen: pages run edge to edge and the footer is
   inset from the screen edge like the pages are from theirs. */
.pdf-preview--fill {
  height: 100%;

  .pdf-preview__pages {
    border-radius: 0;
  }

  .pdf-preview__footer {
    padding: 0.5rem 0.75rem;
  }
}
</style>
